import type { AutomatedCampaignPayload, Campaign, MetabaseCampaign } from '@/shared/types/campaign';
import { fromApiBrDate, toApiBrDate } from './campaign-dates';

/** Documento de campanha automatizada na legacy-api. */
export interface LegacyCampaign {
  _id: string;
  nome?: string;
  mensagem?: string;
  created_at?: string;
  updated_at?: string;
  ativo?: boolean;
  total_recebidos?: number;
  ultimo_status?: string;
  ultimo_erro?: string | null;
  tipo_campanha?: string;
  tipo_disparo?: string;
  atividade?: string;
  frequencia?: string | number;
  frequencia_dia?: string | number;
  frequencia_unico?: string;
  frequencia_horas?: string;
  frequencia_dia_semana?: string;
  expirar?: boolean;
  dt_inicio?: string;
  dt_fim?: string;
  performance?: number;
  plataforma?: string;
  genero?: string;
  faixa_etaria?: string;
  loja?: string;
  estado?: string;
  cidade?: string;
  clusters?: unknown[];
  valor_min?: number | string;
  valor_max?: number | string;
  mais_informacao?: string;
  segmentationId?: string;
}

export interface LegacyMetabaseCampaign {
  _id: string;
  name?: string;
  description?: string;
  updated?: string;
  received?: number;
  users?: string[];
  data_disparo?: string;
  horario?: string;
  performance?: number;
}

export function mapLegacyCampaign(row: LegacyCampaign): Campaign {
  return {
    _id: String(row._id),
    name: row.nome ?? '',
    message: row.mensagem ?? '',
    description: row.mensagem ?? '',
    created_at: row.created_at,
    updated: row.created_at ?? row.updated_at,
    received: row.total_recebidos ?? 0,
    total_recebidos: row.total_recebidos ?? 0,
    ultimo_status: row.ultimo_status,
    ultimo_erro: row.ultimo_erro,
    active: row.ativo ?? false,
    tipo_campanha: row.tipo_campanha,
    tipo_disparo: row.tipo_disparo,
    atividade: row.atividade,
    frequencia: row.frequencia != null ? String(row.frequencia) : undefined,
    frequencia_dia: row.frequencia_dia,
    frequencia_unico: row.frequencia_unico,
    frequencia_horas: row.frequencia_horas,
    frequencia_dia_semana: row.frequencia_dia_semana,
    expirar: row.expirar,
    performance: row.performance,
    plataforma: row.plataforma,
    genero: row.genero,
    faixa_etaria: row.faixa_etaria,
  };
}

export function mapLegacyMetabaseCampaign(row: LegacyMetabaseCampaign): MetabaseCampaign {
  return {
    _id: String(row._id),
    name: row.name ?? '',
    description: row.description ?? '',
    updated: row.updated,
    received: row.received ?? 0,
    users: row.users ?? [],
    data_disparo: row.data_disparo,
    horario: row.horario,
    performance: row.performance,
  };
}

/** '' em Date/Number da API vira CastError e o documento inteiro (inclusive frequencia) não grava. */
function emptyToNull(value?: string | number | null): string | number | null {
  if (value == null) return null;
  if (typeof value === 'string' && !value.trim()) return null;
  return value;
}

export function mapToLegacyCampaignBody(
  idPassbook: string,
  payload: AutomatedCampaignPayload,
): Record<string, unknown> {
  const frequenciaHoras = payload.frequencia_horas?.trim()
    ? payload.frequencia_horas.trim().padStart(5, '0')
    : '';

  let store = payload.stores ?? '';
  if (Array.isArray(store)) {
    store = store.filter(Boolean).join(',');
  }

  // Segmentação e filtros clássicos são mutuamente exclusivos (ver
  // models/campaign.js na API: "uma campanha usa um OU outro, nunca os dois").
  // O formulário apenas ESCONDE os campos clássicos quando há uma segmentação
  // escolhida — ele não limpa o estado —, então sem este corte uma campanha
  // editada continuaria mandando o gênero/loja/plataforma antigos, que o
  // backend soma ao público da segmentação (ele só sobrescreve `serialNumber`).
  // Resultado: disparo para segmentação ∩ filtros antigos, menor que o total
  // exibido no card "Leads na segmentação". Sem segmentação nada muda aqui.
  const usaSegmentacao = Boolean(payload.segmentationId);

  const frequencia = String(payload.frequencia ?? '1');
  const clustersRaw = usaSegmentacao ? '[]' : payload.clusters_data ?? '[]';

  return {
    action: payload.action,
    idcampanha: payload.action === 'edit' ? payload.id : undefined,
    id_passbook: idPassbook,
    nome: payload.nome,
    dt_inicio: emptyToNull(payload.dt_inicio),
    dt_fim: emptyToNull(payload.dt_fim),
    expirar: payload.expirar,
    performance: payload.performance ?? 3,
    plataforma: usaSegmentacao ? 'all' : payload.plataforma ?? '',
    tipo_disparo: payload.tipo_disparo,
    atividade: usaSegmentacao ? '' : payload.atividade ?? '',
    faixa_etaria: usaSegmentacao ? '' : payload.faixa_etaria ?? '',
    valor_min: emptyToNull(payload.valor_de),
    valor_max: emptyToNull(payload.valor_ate),
    frequencia,
    mensagem: payload.description,
    frequencia_dia: frequencia === '30' ? emptyToNull(payload.frequencia_dia) : null,
    /** Cron legado espera `dd/mm/yyyy` (não ISO). */
    frequencia_unico: frequencia === '31' ? toApiBrDate(payload.frequencia_unico) : '',
    frequencia_horas: frequenciaHoras,
    frequencia_dia_semana: frequencia === '7' ? (payload.frequencia_dia_semana ?? '') : '',
    ativo: payload.ativo,
    tipo_campanha: payload.tipo_campanha,
    genero: usaSegmentacao ? '' : payload.genero ?? '',
    mais_informacao: payload.mais_informacao ?? '',
    store: usaSegmentacao ? '' : store,
    estado: usaSegmentacao ? '' : payload.state === '0' ? '' : payload.state ?? '',
    cidade: usaSegmentacao ? '' : payload.city === '0' ? '' : payload.city ?? '',
    clusters: clustersRaw === '{}' ? '[]' : clustersRaw,
    // Ausente/undefined faz o backend desassociar (ver controllers/campaign.js) --
    // nunca manda string vazia, senão o Mongoose casting rejeitaria (ObjectId).
    segmentationId: payload.segmentationId || undefined,
  };
}

export function legacyCampaignToPayload(
  campaign: LegacyCampaign,
  id?: string,
): AutomatedCampaignPayload {
  return {
    action: id ? 'edit' : 'insert',
    id,
    ativo: campaign.ativo ?? false,
    nome: campaign.nome ?? '',
    tipo_campanha: campaign.tipo_campanha ?? 'C',
    tipo_disparo: campaign.tipo_disparo ?? 'push',
    frequencia: String(campaign.frequencia ?? '1'),
    frequencia_dia: campaign.frequencia_dia != null ? String(campaign.frequencia_dia) : '',
    frequencia_unico: fromApiBrDate(campaign.frequencia_unico),
    frequencia_horas: campaign.frequencia_horas ?? '',
    frequencia_dia_semana: campaign.frequencia_dia_semana ?? '',
    expirar: campaign.expirar ?? false,
    dt_inicio: fromApiBrDate(campaign.dt_inicio ? String(campaign.dt_inicio) : '') ||
      (campaign.dt_inicio ? String(campaign.dt_inicio).slice(0, 10) : ''),
    dt_fim: fromApiBrDate(campaign.dt_fim ? String(campaign.dt_fim) : '') ||
      (campaign.dt_fim ? String(campaign.dt_fim).slice(0, 10) : ''),
    performance: campaign.performance ?? 3,
    // 'all' (não '') — mesmo motivo do default em defaultAutomatedPayload().
    plataforma: campaign.plataforma ?? 'all',
    atividade: campaign.atividade ?? '',
    faixa_etaria: campaign.faixa_etaria ?? '',
    genero: campaign.genero ?? '',
    description: campaign.mensagem ?? '',
    mais_informacao: campaign.mais_informacao ?? '',
    stores: campaign.loja ?? '',
    state: campaign.estado ?? '',
    city: campaign.cidade ?? '',
    clusters_data: JSON.stringify(campaign.clusters ?? []),
    valor_de: campaign.valor_min != null ? String(campaign.valor_min) : '',
    valor_ate: campaign.valor_max != null ? String(campaign.valor_max) : '',
    segmentationId: campaign.segmentationId,
  };
}
